import { StyledLeadsPageRoot } from "app/pages/sales-intelligence/common-components/layout";
import LeadGenNotFoundModalContainer from "app/pages/sales-intelligence/common-components/modals/LeadGenNotFoundModal/LeadGenNotFoundModalContainer";
import { FiltersConfigProvider } from "app/pages/sales-intelligence/modules/advanced-search-filtering/context/FiltersConfigContext";
import { AppsFlowProvider } from "app/pages/sales-intelligence/modules/apps-search-filters/context/AppsFlowContext";
import { useAppsFlowStrategyFromFilters } from "app/pages/sales-intelligence/modules/apps-search-filters/hooks/useAppsFlowStrategyFromFilters";
import { ScoringRuleWizardHost } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/ScoringRuleWizardHost";
import { useIsLeadsCriticalDataLoading } from "app/pages/sales-intelligence/modules/leads-search/hooks/useIsLeadsCriticalDataLoading";
import SavedSearchModalContainer from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/containers/SavedSearchModalContainer/SavedSearchModalContainer";
import SaveSearchTriggerModalMount from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/containers/SaveSearchTriggerModal/SaveSearchTriggerModalMount";
import { useApplyInitialFilters } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useApplyInitialFilters";
import {
    hasInitialSeed,
    type InitialLeadsSeed,
} from "app/pages/sales-intelligence/modules/leads-search-filters/utils/initialSeed";
import { useApplyTerritoryFilters } from "app/pages/sales-intelligence/modules/teams/hooks/useApplyTerritoryFilters";
import useWorkspacesCall from "app/pages/sales-intelligence/sub-modules/common/hooks/useWorkspacesCall";
import { useIntegrationsSyncCall } from "app/pages/sales-intelligence/sub-modules/management/hooks/integration-sync/useIntegrationsSyncCall";
import { useAllCrmMappingFieldsCall } from "app/pages/sales-intelligence/sub-modules/management/hooks/useAllCrmMappingFieldsCall";
import { useAllCrmStatusesCall } from "app/pages/sales-intelligence/sub-modules/management/hooks/useAllCrmStatusesCall";
import useSuppressionListsCall from "app/pages/sales-intelligence/sub-modules/management/hooks/useSuppressionListsCall";
import SidebarContainer, {
    SidebarTrackingSectionName,
} from "app/pages/sales-intelligence/sub-modules/right-sidebar/components/Sidebar/SidebarContainer";
import withSignalsManagement from "app/pages/sales-intelligence/sub-modules/signals/hoc/withSignalsManagement";

import { useDemoFromRouteParam } from "../../hooks/useDemoFromRouteParam";
import { useFiltersConfig } from "../../hooks/useFiltersConfig";
import { useIsTerritoryEligible } from "../../hooks/useIsTerritoryEligible";
import { useMarkInitialFiltersReady } from "../../hooks/useMarkInitialFiltersReady";
import { useSavedSearchFetch } from "../../hooks/useSavedSearchFetch";
import { useUrlSeededFilterValues } from "../../hooks/useUrlSeededFilterValues";
import PageHeader from "../PageHeader/PageHeader";
import PageMainContent from "../PageMainContent/PageMainContent";

const PageRootContent = ({ initialSeed }: { initialSeed: InitialLeadsSeed }) => {
    const isLeadsCriticalDataLoading = useIsLeadsCriticalDataLoading();
    const isTerritoryEligible = useIsTerritoryEligible();

    const { filtersList, status, filtersSearchConfigList, categoriesList } = useFiltersConfig();

    // Seed + defaults are written once here at the page root, where the URL seed is a render value at the
    // same level as this reader (no cross-component effect ordering).
    useApplyInitialFilters({ filtersList, status }, initialSeed);
    useApplyTerritoryFilters(isTerritoryEligible, filtersList, hasInitialSeed(initialSeed));
    useMarkInitialFiltersReady(status);

    // Runs after the readiness mark above: a `?demo=` request waits for that same signal before taking effect.
    useDemoFromRouteParam();

    return (
        <FiltersConfigProvider
            filtersList={filtersList}
            status={status}
            filtersSearchConfigList={filtersSearchConfigList}
            categoriesList={categoriesList}
        >
            <StyledLeadsPageRoot>
                <PageHeader />
                <PageMainContent />

                {isLeadsCriticalDataLoading ? null : (
                    <SidebarContainer trackingSectionName={SidebarTrackingSectionName.LeadGen} />
                )}
                <LeadGenNotFoundModalContainer />
                <SavedSearchModalContainer />
                <SaveSearchTriggerModalMount />
                <ScoringRuleWizardHost />
            </StyledLeadsPageRoot>
        </FiltersConfigProvider>
    );
};

const PageRootContainer = () => {
    useSavedSearchFetch();

    const initialSeed = useUrlSeededFilterValues();

    useWorkspacesCall();
    useAllCrmStatusesCall();
    useAllCrmMappingFieldsCall();
    useIntegrationsSyncCall();
    useSuppressionListsCall();

    const appsFlowStrategy = useAppsFlowStrategyFromFilters();

    return (
        <AppsFlowProvider value={appsFlowStrategy}>
            <PageRootContent initialSeed={initialSeed} />
        </AppsFlowProvider>
    );
};

export default withSignalsManagement(PageRootContainer);
